import * as Sentry from '@sentry/vue';
import { toApiError } from '@/helpers/ApiError';

const REQUEST_TIMEOUT_MS = 60000;

/**
 * Tamano del cuerpo en KB, para saber si un fallo va ligado al peso del envio.
 *
 * Solo mide: nunca se reporta el contenido, que lleva cedula, correo y los
 * documentos del candidato.
 */
const pesoEnKb = (data: unknown): number => {
  if (data == null) return 0;
  const texto = typeof data === 'string' ? data : JSON.stringify(data);
  return Math.round((texto?.length ?? 0) / 1024);
};

const getAxios = async () => {
  const axios = await import('axios');
  const instance = axios.default.create({
    baseURL: import.meta.env.VITE_API_URL,
    timeout: REQUEST_TIMEOUT_MS,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Marca de salida: sin ella no se puede distinguir el fallo inmediato -la
  // peticion nunca sale- del que agota los 60 s. Son causas distintas.
  instance.interceptors.request.use((config) => ({
    ...config,
    metadata: { inicio: Date.now() },
  }));

  // Normaliza el error y lo rechaza. No muestra alertas: cada acción del store
  // decide si informa al usuario o si intenta recuperarse (ver uploadOnlyDocument).
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      const apiError = toApiError(error);

      if (apiError.status >= 500 || apiError.status === 0) {
        const inicio = (error?.config as any)?.metadata?.inicio;
        const endpoint = String(error?.config?.url ?? 'desconocido').split('?')[0];

        Sentry.captureException(apiError, {
          tags: {
            apiStatus: String(apiError.status),
            apiCode: apiError.code,
            correlationId: apiError.correlationId ?? 'none',
            apiEndpoint: endpoint,
          },
          extra: {
            // Con estos dos datos se puede comprobar si los envios grandes
            // fallan mas que los ligeros, y si lo hacen al salir o al agotar
            // el timeout. Ver marinoevl/projects-docs#326.
            pesoPeticionKb: pesoEnKb(error?.config?.data),
            duracionMs: inicio ? Date.now() - inicio : null,
            timeoutMs: REQUEST_TIMEOUT_MS,
          },
        });
      }

      return Promise.reject(apiError);
    },
  );

  return instance;
};

export default getAxios;
